import React, { useState } from "react";
import {
  Alert,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Typography,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HourglassEmpty as HourglassEmptyIcon,
} from "@mui/icons-material";
import useAuth from "../../hooks/useAuth";
import { useAlert } from "../../context/AlertProvider";
import {
  useListPendingUsersQuery,
  useApprovePendingUserMutation,
  useRejectPendingUserMutation,
} from "../../store/pendingUserApi";
import { COLORS, themedTableHeadCellSx } from "../../styles/uiConstants";
import { useClinicTheme } from "../../theme";
import { PageHeaderBar, PromptDialog } from "../../components/molecules";
import { DataTableCard } from "../../components/organisms";

const STATUS_CONFIG = {
  Beklemede: { label: "Beklemede", bg: COLORS.warningLight, color: COLORS.warningDarker },
  Onaylandı: { label: "Onaylandı", bg: COLORS.successLight, color: COLORS.successDarker },
  Reddedildi: { label: "Reddedildi", bg: COLORS.errorLight, color: COLORS.errorDark },
  "Süresi Doldu": { label: "Süresi Doldu", bg: COLORS.slate200, color: COLORS.slate700 },
};

const PAGE_LAYOUT_SX = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  bgcolor: COLORS.slate50,
};

const CONTENT_SX = {
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  gap: 2,
  p: { xs: 2, md: 3 },
  overflow: "hidden",
};

const TABLE_HEADERS = ["Ad", "Soyad", "E-posta", "Rol Şablonu", "Klinik", "Durum", "Kayıt Tarihi", "İşlemler"];

export default function PendingUsers() {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { showSuccess, showError } = useAlert();
  const { palette } = useClinicTheme();
  const headCellSx = themedTableHeadCellSx(palette);

  const { data, isLoading, error } = useListPendingUsersQuery({ token }, { skip: !token });
  const [approvePendingUser, { isLoading: isApproving }] = useApprovePendingUserMutation();
  const [rejectPendingUser, { isLoading: isRejecting }] = useRejectPendingUserMutation();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Reject dialog state
  const [rejectDialog, setRejectDialog] = useState({ open: false, user: null });

  const rawPending = data?.data?.pendingUsers || data?.pendingUsers || data?.data;
  const pendingUsers = Array.isArray(rawPending) ? rawPending : [];

  const handleApprove = async (user) => {
    try {
      const res = await approvePendingUser({ token, id: user._id }).unwrap();
      if (res?.success) {
        showSuccess(`${user.name} ${user.surname} onaylandı.`);
      } else {
        showError(res?.message || "Onaylama başarısız.");
      }
    } catch (err) {
      showError(err?.data?.message || "Onaylama sırasında hata oluştu.");
    }
  };

  const openRejectDialog = (user) => {
    setRejectDialog({ open: true, user });
  };

  const closeRejectDialog = () => {
    setRejectDialog({ open: false, user: null });
  };

  const handleReject = async (rejectionReason) => {
    const { user } = rejectDialog;
    if (!user) return;
    try {
      const res = await rejectPendingUser({
        token,
        id: user._id,
        rejectionReason,
      }).unwrap();
      if (res?.success) {
        showSuccess(`${user.name} ${user.surname} reddedildi.`);
        closeRejectDialog();
      } else {
        showError(res?.message || "Reddetme başarısız.");
      }
    } catch (err) {
      showError(err?.data?.message || "Reddetme sırasında hata oluştu.");
    }
  };

  const pagedUsers = pendingUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={PAGE_LAYOUT_SX}>
      <PageHeaderBar
        icon={<HourglassEmptyIcon sx={{ fontSize: 22 }} />}
        title="Bekleyen Kullanıcılar"
        subtitle="Onay bekleyen kullanıcı başvurularını yönetin"
        action={
          <Chip
            icon={<HourglassEmptyIcon sx={{ fontSize: 16 }} />}
            label={`${pendingUsers.length} Başvuru`}
            sx={{
              fontWeight: 700,
              bgcolor: COLORS.warningLight,
              color: COLORS.warningDarker,
            }}
          />
        }
      />

      <Box sx={CONTENT_SX}>
        {error && (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            Bekleyen kullanıcılar yüklenirken hata oluştu.
          </Alert>
        )}

        <DataTableCard
      loading={Boolean(isLoading)}
          empty={!isLoading && pagedUsers.length === 0}
          emptyIcon={<HourglassEmptyIcon fontSize="inherit" />}
          emptyTitle="Bekleyen kullanıcı bulunamadı."
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={pendingUsers.length}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {TABLE_HEADERS.map((h) => (
                  <TableCell key={h} sx={headCellSx}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : pagedUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ height: 1, py: 0 }} />
                </TableRow>
              ) : (
                pagedUsers.map((user) => {
                  const status = user.status || "Beklemede";
                  const statusCfg = STATUS_CONFIG[status] || STATUS_CONFIG.Beklemede;
                  return (
                    <TableRow
                      key={user._id}
                      hover
                      sx={{ "&:last-child td": { borderBottom: 0 } }}
                    >
                      <TableCell sx={{ fontWeight: 600, color: COLORS.slate800 }}>
                        {user.name || "-"}
                      </TableCell>
                      <TableCell sx={{ color: COLORS.slate700 }}>
                        {user.surname || "-"}
                      </TableCell>
                      <TableCell sx={{ color: COLORS.slate600 }}>
                        {user.email || "-"}
                      </TableCell>
                      <TableCell sx={{ color: COLORS.slate600 }}>
                        {user.roleTemplateName || user.roleTemplate?.name || "-"}
                      </TableCell>
                      <TableCell sx={{ color: COLORS.slate600 }}>
                        {user.clinicName || user.clinic?.name || "-"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={statusCfg.label}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontSize: 11,
                            bgcolor: statusCfg.bg,
                            color: statusCfg.color,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: COLORS.slate500, fontSize: 13 }}>
                        {user.createdAt
                          ? new Date(user.createdAt).toLocaleDateString("tr-TR")
                          : "-"}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Tooltip title="Onayla">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleApprove(user)}
                                disabled={isApproving || status !== "Beklemede"}
                                sx={{ color: COLORS.success }}
                              >
                                {isApproving ? (
                                  <CircularProgress size={18} />
                                ) : (
                                  <CheckCircleIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Reddet">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => openRejectDialog(user)}
                                disabled={isRejecting || status !== "Beklemede"}
                                sx={{ color: COLORS.error }}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </DataTableCard>
      </Box>

      <PromptDialog
        open={rejectDialog.open}
        onClose={closeRejectDialog}
        onConfirm={handleReject}
        title="Başvuruyu Reddet"
        message={
          <Typography variant="body2" sx={{ color: COLORS.slate600 }}>
            <strong>
              {rejectDialog.user?.name} {rejectDialog.user?.surname}
            </strong>{" "}
            kullanıcısının başvurusunu reddetmek üzeresiniz. Lütfen bir red gerekçesi girin.
          </Typography>
        }
        inputLabel="Red Gerekçesi"
        placeholder="Red gerekçesini buraya yazın..."
        required
        confirmText="Reddet"
        confirmVariant="error"
        loading={isRejecting}
        loadingText="Reddet"
      />
    </Box>
  );
}
